import { HttpClient } from '../api/HttpClient';
import { API_ENDPOINTS } from '../api/ApiConfig';
import type {
  Episode,
  EpisodeCreateRequest,
  EpisodeDetailResponse,
  EpisodeUpdateRequest,
  EpisodesPage,
  EpisodesQueryParams,
} from '../../types/api/episode';
import type {
  CompleteMultipartUploadRequest,
  InitiateMultipartUploadRequest,
  InitiateMultipartUploadResponse,
  VideoMultipartPresignResponse,
  VideoMultipartRegenerateRequest,
  VideoMultipartStatusResponse,
  VideoVariant,
} from '../../types/api/movie';

/**
 * Episode Service
 *
 * Handles all episode-related API operations under a Drama/Series (dramaId).
 */
export class EpisodeService {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = HttpClient.getInstance();
  }

  /**
   * Get paginated list of episodes for a drama/series.
   */
  async getEpisodes(dramaId: string, params: EpisodesQueryParams = {}): Promise<EpisodesPage> {
    const queryParams = new URLSearchParams();

    if (params.page !== undefined) {
      queryParams.append('page', params.page.toString());
    }
    if (params.size !== undefined) {
      queryParams.append('size', params.size.toString());
    }
    if (params.season !== undefined) {
      queryParams.append('season', params.season.toString());
    }
    if (params.keyword) {
      queryParams.append('keyword', params.keyword);
    }
    if (params.status) {
      queryParams.append('status', String(params.status));
    }

    const baseUrl = API_ENDPOINTS.DRAMAS.PUBLIC_EPISODES(dramaId);
    const url =
      queryParams.toString().length > 0 ? `${baseUrl}?${queryParams.toString()}` : baseUrl;

    const response = await this.httpClient.get<EpisodesPage>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch episodes');
    }

    return response.data;
  }

  /**
   * Get single episode detail.
   */
  async getEpisode(dramaId: string, episodeId: string): Promise<EpisodeDetailResponse> {
    const url = API_ENDPOINTS.DRAMAS.PUBLIC_EPISODE_DETAILS(dramaId, episodeId);
    const response = await this.httpClient.get<EpisodeDetailResponse>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch episode');
    }

    return response.data;
  }

  /**
   * Create a new episode with optional thumbnail upload (multipart).
   *
   * Uses:
   * - `data`: JSON string (EpisodeCreateRequest)
   * - `thumbnail`: optional image file
   */
  async createEpisodeWithThumbnail(
    dramaId: string,
    payload: EpisodeCreateRequest,
    thumbnailFile?: File
  ): Promise<Episode> {
    console.log('createEpisodeWithThumbnail - dramaId param:', dramaId);
    console.log('createEpisodeWithThumbnail - payload received:', payload);
    
    const body: EpisodeCreateRequest = {
      ...payload,
      dramaId,
    };
    
    console.log('createEpisodeWithThumbnail - body being sent:', body);
    console.log('createEpisodeWithThumbnail - URL:', API_ENDPOINTS.DRAMAS.ADMIN_CREATE_EPISODE_MULTIPART(dramaId));

    const formData = new FormData();

    const jsonBlob = new Blob([JSON.stringify(body)], {
      type: 'application/json',
    });
    formData.append('data', jsonBlob, 'data.json');

    if (thumbnailFile) {
      formData.append('thumbnail', thumbnailFile, thumbnailFile.name);
    }

    const response = await this.httpClient.request<Episode>({
      method: 'POST',
      url: API_ENDPOINTS.DRAMAS.ADMIN_CREATE_EPISODE_MULTIPART(dramaId),
      data: formData,
      headers: {
        // HttpClient + RequestInterceptor are FormData-aware and will strip
        // Content-Type so axios/browser set correct multipart boundary.
        'Content-Type': 'multipart/form-data',
      },
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to create episode');
    }

    return response.data;
  }

  /**
   * Create episode using JSON-only endpoint (thumbnailUrl provided).
   */
  async createEpisodeWithJson(dramaId: string, payload: EpisodeCreateRequest): Promise<Episode> {
    const body: EpisodeCreateRequest = {
      ...payload,
      dramaId,
    };

    const response = await this.httpClient.post<Episode>(
      API_ENDPOINTS.DRAMAS.ADMIN_CREATE_EPISODE_JSON(dramaId),
      body
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to create episode');
    }

    return response.data;
  }

  /**
   * Update episode metadata.
   */
  async updateEpisode(
    dramaId: string,
    episodeId: string,
    payload: EpisodeUpdateRequest
  ): Promise<Episode> {
    const url = API_ENDPOINTS.DRAMAS.ADMIN_UPDATE_EPISODE(dramaId, episodeId);
    const response = await this.httpClient.put<Episode>(url, payload);

    if (!response.success) {
      throw new Error(response.message || 'Failed to update episode');
    }

    return response.data;
  }

  /**
   * Soft delete an episode.
   */
  async softDeleteEpisode(dramaId: string, episodeId: string): Promise<void> {
    const url = API_ENDPOINTS.DRAMAS.ADMIN_SOFT_DELETE_EPISODE(dramaId, episodeId);
    const response = await this.httpClient.delete<{ message: string }>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete episode');
    }
  }

  /**
   * Permanently delete an episode.
   */
  async permanentDeleteEpisode(dramaId: string, episodeId: string): Promise<void> {
    const url = API_ENDPOINTS.DRAMAS.ADMIN_PERMANENT_DELETE_EPISODE(dramaId, episodeId);
    const response = await this.httpClient.delete<{ message: string }>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to permanently delete episode');
    }
  }

  /**
   * Get seasons that have episodes for a given drama/series.
   */
  async getSeasons(dramaId: string): Promise<number[]> {
    const url = API_ENDPOINTS.DRAMAS.PUBLIC_SEASONS(dramaId);
    const response = await this.httpClient.get<number[]>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch seasons');
    }

    return response.data;
  }

  /**
   * Initiate multipart video upload for a specific episode.
   *
   * Mirrors movie-level multipart initiation but scoped to
   * /admin/dramas/{dramaId}/episodes/{episodeId}/videos/initiate-multipart.
   */
  async initiateMultipartVideoUpload(
    dramaId: string,
    episodeId: string,
    payload: InitiateMultipartUploadRequest
  ): Promise<InitiateMultipartUploadResponse> {
    const url = API_ENDPOINTS.DRAMAS.ADMIN_EPISODE_VIDEOS_INITIATE_MULTIPART(dramaId, episodeId);
    const response = await this.httpClient.post<InitiateMultipartUploadResponse>(url, payload);

    if (!response.success) {
      throw new Error(response.message || 'Failed to initiate episode video upload');
    }

    return response.data;
  }

  /**
   * Complete multipart video upload for a specific episode.
   */
  async completeMultipartVideoUpload(
    dramaId: string,
    episodeId: string,
    payload: CompleteMultipartUploadRequest
  ): Promise<VideoVariant> {
    const url = API_ENDPOINTS.DRAMAS.ADMIN_EPISODE_VIDEOS_COMPLETE_MULTIPART(dramaId, episodeId);
    const response = await this.httpClient.post<VideoVariant>(url, payload);

    if (!response.success) {
      throw new Error(response.message || 'Failed to complete episode video upload');
    }

    return response.data;
  }

  /**
   * Get multipart upload status for a specific episode video upload.
   */
  async getMultipartUploadStatus(
    dramaId: string,
    episodeId: string,
    key: string,
    uploadId: string
  ): Promise<VideoMultipartStatusResponse> {
    const queryParams = new URLSearchParams({
      key,
      uploadId,
    });

    const baseUrl = API_ENDPOINTS.DRAMAS.ADMIN_EPISODE_VIDEOS_MULTIPART_STATUS(dramaId, episodeId);
    const url = `${baseUrl}?${queryParams.toString()}`;

    const response = await this.httpClient.get<VideoMultipartStatusResponse>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch episode multipart upload status');
    }

    return response.data;
  }

  /**
   * Regenerate presigned part URLs for an existing episode multipart upload.
   */
  async regenerateMultipartPartUrls(
    dramaId: string,
    episodeId: string,
    request: VideoMultipartRegenerateRequest
  ): Promise<VideoMultipartPresignResponse> {
    const url = API_ENDPOINTS.DRAMAS.ADMIN_EPISODE_VIDEOS_REGENERATE_PART_URLS(dramaId, episodeId);
    const response = await this.httpClient.post<VideoMultipartPresignResponse>(url, request);

    if (!response.success) {
      throw new Error(response.message || 'Failed to regenerate episode multipart part URLs');
    }

    return response.data;
  }

  /**
   * Abort an in-progress multipart upload for an episode video.
   */
  async abortMultipartVideoUpload(
    dramaId: string,
    episodeId: string,
    params: { key: string; uploadId: string }
  ): Promise<void> {
    const queryParams = new URLSearchParams({
      key: params.key,
      uploadId: params.uploadId,
    });

    const baseUrl = API_ENDPOINTS.DRAMAS.ADMIN_EPISODE_VIDEOS_ABORT(dramaId, episodeId);
    const url = `${baseUrl}?${queryParams.toString()}`;

    const response = await this.httpClient.delete<{ message: string }>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to abort episode video upload');
    }
  }

  /**
   * Direct upload for small/testing episode videos.
   *
   * Accepts multipart/form-data with `file` and optional video metadata.
   */
  async uploadEpisodeVideoDirect(
    dramaId: string,
    episodeId: string,
    formData: FormData
  ): Promise<VideoVariant> {
    const url = API_ENDPOINTS.DRAMAS.ADMIN_EPISODE_VIDEOS_UPLOAD_DIRECT(dramaId, episodeId);

    const response = await this.httpClient.request<VideoVariant>({
      method: 'POST',
      url,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to upload episode video');
    }

    return response.data;
  }

  /**
   * Delete a specific video variant attached to an episode.
   */
  async deleteEpisodeVideo(dramaId: string, episodeId: string, fileId: string): Promise<void> {
    const url = API_ENDPOINTS.DRAMAS.ADMIN_EPISODE_VIDEOS_DELETE(dramaId, episodeId, fileId);
    const response = await this.httpClient.delete<{ message: string }>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete episode video');
    }
  }
}